import { api } from "../api";
import { el } from "../lib/dom";
import { errorBox } from "../lib/ui";
import type { AuditFilters, AuditPage, DashboardStats, ProvisionRow } from "../types";

// Chart.js is loaded lazily + guarded: in jsdom (tests) there's no 2d canvas
// context, so charts are skipped while cards/tables still render.
let ChartLib: unknown = null;
async function getChart(): Promise<any> {
  if (ChartLib) return ChartLib;
  const m = await import("chart.js");
  m.Chart.register(...m.registerables);
  ChartLib = m.Chart;
  return ChartLib;
}
function canDraw(): boolean {
  try {
    return !!document.createElement("canvas").getContext("2d");
  } catch {
    return false;
  }
}

const BRAND = {
  violet: "#693af4", purple: "#853eff", indigo: "#33139f", blue: "#34b4ff",
  slate: "#94a3b8", ok: "#0e9f6e", fail: "#dc2626", warn: "#f59e0b",
};
const DAYS = [
  { k: 1, label: "Today" }, { k: 7, label: "7 days" },
  { k: 30, label: "30 days" }, { k: 90, label: "90 days" },
];
const EVENT_TYPES = ["login", "launch", "app_view", "provision", "admin_action", "error"];

function fmtTime(iso?: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

// Actor (who acted) → Impacted (who it was done to). For self-service events the two
// match, so we show just the one name; for admin actions we show "actor → subject".
function auditWho(r: { user_email?: string | null; subject_email?: string | null }): HTMLElement {
  const actor = r.user_email || "—";
  const subject = r.subject_email || null;
  if (!subject || subject === r.user_email) return el("span", {}, [actor]);
  return el("span", {}, [actor, el("span", { class: "audit-arrow" }, [" → "]), subject]);
}

export async function renderInsightsDashboard(body: HTMLElement): Promise<void> {
  body.append(el("h2", { class: "admin-h" }, ["Insights & Audit"]));

  const filters: AuditFilters = { days: 7, limit: 25, offset: 0 };
  const charts: any[] = [];

  // hosts
  const barHost = el("div", { class: "audit-filters" });
  const cardsHost = el("div", { class: "stat-grid" });
  const chartsHost = el("div", { class: "audit-charts" });
  const backlogHost = el("div", { class: "audit-panel" });
  const tableHost = el("div", { class: "audit-panel" });
  body.append(barHost, cardsHost, chartsHost, backlogHost, tableHost);

  // ---- filter bar ----
  const daySeg = el("div", { class: "audit-seg" },
    DAYS.map((d) =>
      el("button", {
        type: "button",
        class: d.k === filters.days ? "on" : "",
        onclick: () => { filters.days = d.k; filters.offset = 0; syncSeg(); void refresh(); },
      }, [d.label]),
    ),
  );
  function syncSeg() {
    [...daySeg.children].forEach((b, i) =>
      (b as HTMLElement).classList.toggle("on", DAYS[i].k === filters.days),
    );
  }
  const evtSel = el("select", { class: "control-input",
    onchange: (e: Event) => { filters.event_type = (e.target as HTMLSelectElement).value || undefined; filters.offset = 0; void refresh(); } },
    [el("option", { value: "" }, ["All events"]), ...EVENT_TYPES.map((t) => el("option", { value: t }, [t]))]);
  const appSel = el("select", { class: "control-input",
    onchange: (e: Event) => { filters.app_key = (e.target as HTMLSelectElement).value || undefined; filters.offset = 0; void refresh(); } },
    [el("option", { value: "" }, ["All apps"])]);
  const outSel = el("select", { class: "control-input",
    onchange: (e: Event) => { filters.outcome = (e.target as HTMLSelectElement).value || undefined; filters.offset = 0; void refresh(); } },
    [el("option", { value: "" }, ["All outcomes"]), el("option", { value: "success" }, ["Success"]), el("option", { value: "failed" }, ["Failed"])]);
  const ctrySel = el("select", { class: "control-input",
    onchange: (e: Event) => { filters.country = (e.target as HTMLSelectElement).value || undefined; filters.offset = 0; void refresh(); } },
    [el("option", { value: "" }, ["All countries"])]);
  const search = el("input", { class: "control-input", type: "search", placeholder: "user email / IP…" }) as HTMLInputElement;
  let searchTimer: ReturnType<typeof setTimeout>;
  search.addEventListener("input", () => {
    clearTimeout(searchTimer);
    searchTimer = setTimeout(() => { filters.q = search.value.trim() || undefined; filters.offset = 0; void refresh(); }, 350);
  });
  barHost.append(daySeg, evtSel, appSel, outSel, ctrySel, search);

  // ---- cards ----
  function paintCards(s: DashboardStats): void {
    const card = (label: string, value: string | number, alert = false) =>
      el("div", { class: `stat-card${alert ? " is-alert" : ""}` }, [
        el("span", { class: "stat-num" }, [String(value)]),
        el("span", { class: "stat-label" }, [label]),
      ]);
    const launches = s.outcome["launch"] ?? { success: 0, failed: 0 };
    const provFail = (s.outcome["provision"] ?? { failed: 0 }).failed ?? 0;
    const launchTotal = launches.success + launches.failed;
    const rate = launchTotal ? ((launches.success / launchTotal) * 100).toFixed(1) + "%" : "—";
    cardsHost.replaceChildren(
      card("Events", s.total),
      card("Logins", s.by_type["login"] ?? 0),
      card("Launches", s.by_type["launch"] ?? 0),
      card("Launch success", rate),
      card("Provision failures", provFail, provFail > 0),
      card("Errors", s.by_type["error"] ?? 0, (s.by_type["error"] ?? 0) > 0),
    );
  }

  // ---- charts ----
  async function paintCharts(s: DashboardStats): Promise<void> {
    charts.forEach((c) => c.destroy());
    charts.length = 0;
    const panels: [string, string][] = [
      ["Activity over time", "cTime"], ["Events by type", "cType"],
      ["Launches per app", "cApp"], ["Outcome (launch & provision)", "cOut"],
      ["Access by location", "cGeo"],
    ];
    chartsHost.replaceChildren(
      ...panels.map(([title, id]) =>
        el("div", { class: "audit-chart" }, [
          el("h3", { class: "insights-sub" }, [title]),
          el("canvas", { id, height: "220" }),
        ]),
      ),
    );
    if (!canDraw()) {
      chartsHost.append(el("p", { class: "muted-sm" }, ["(charts render in the browser)"]));
      return;
    }
    const Chart = await getChart();
    const c = (id: string) => chartsHost.querySelector(`#${id}`) as HTMLCanvasElement;
    charts.push(new Chart(c("cTime"), { type: "line", data: {
      labels: s.series.map((p) => p.day),
      datasets: [
        { label: "Logins", data: s.series.map((p) => p.logins), borderColor: BRAND.violet, tension: 0.35 },
        { label: "Launches", data: s.series.map((p) => p.launches), borderColor: BRAND.blue, tension: 0.35 },
      ],
    }, options: { plugins: { legend: { position: "bottom" } } } }));
    const types = Object.keys(s.by_type);
    charts.push(new Chart(c("cType"), { type: "doughnut", data: {
      labels: types, datasets: [{ data: types.map((t) => s.by_type[t]),
        backgroundColor: [BRAND.violet, BRAND.indigo, BRAND.blue, BRAND.warn, "#0e6f6e", BRAND.fail] }],
    }, options: { cutout: "58%", plugins: { legend: { position: "right" } } } }));
    charts.push(new Chart(c("cApp"), { type: "bar", data: {
      labels: s.per_app.map((r) => r.app_key),
      datasets: [{ data: s.per_app.map((r) => r.launches), backgroundColor: BRAND.violet, borderRadius: 6 }],
    }, options: { indexAxis: "y", plugins: { legend: { display: false } } } }));
    const oKeys = Object.keys(s.outcome);
    charts.push(new Chart(c("cOut"), { type: "bar", data: {
      labels: oKeys,
      datasets: [
        { label: "Success", data: oKeys.map((k) => s.outcome[k].success), backgroundColor: BRAND.ok, stack: "s" },
        { label: "Failed", data: oKeys.map((k) => s.outcome[k].failed), backgroundColor: BRAND.fail, stack: "s" },
      ],
    }, options: { plugins: { legend: { position: "bottom" } }, scales: { x: { stacked: true }, y: { stacked: true } } } }));
    charts.push(new Chart(c("cGeo"), { type: "bar", data: {
      labels: s.geo.map((g) => g.country),
      datasets: [{ data: s.geo.map((g) => g.percent), backgroundColor: BRAND.blue, borderRadius: 6 }],
    }, options: { plugins: { legend: { display: false }, tooltip: { callbacks: { label: (x: any) => x.parsed.y + "%" } } } } }));
  }

  // ---- provisioning backlog ----
  function paintBacklog(rows: ProvisionRow[]): void {
    const retryBtn = el("button", { class: "btn-primary-sm", type: "button",
      onclick: () => { retryBtn.setAttribute("disabled", ""); retryBtn.textContent = "Retrying…";
        api.adminProvisionsRetry().then(() => refresh()).catch(() => { retryBtn.removeAttribute("disabled"); retryBtn.textContent = "Retry all now"; }); } },
      ["Retry all now"]);
    const head = el("div", { class: "audit-panel-head" }, [
      el("div", {}, [el("h3", { class: "insights-sub" }, ["Provisioning retry backlog"]),
        el("p", { class: "muted-sm" }, ["In-app access that failed after 3 attempts. Auto-retries on schedule; Hub access is unaffected."])]),
      ...(rows.length ? [retryBtn] : []),
    ]);
    const table = el("table", { class: "audit-table" }, [
      el("thead", {}, [el("tr", {}, ["User", "App", "Attempts", "Last error", "Next retry", "Status"].map((h) => el("th", {}, [h])))]),
      el("tbody", {}, rows.length
        ? rows.map((r) => el("tr", {}, [
            el("td", {}, [r.user_email]), el("td", {}, [r.app_name || r.app_key]),
            el("td", {}, [String(r.attempts)]), el("td", { class: "mono-sm" }, [r.last_error || "—"]),
            el("td", {}, [r.next_retry_at ? fmtTime(r.next_retry_at) : "—"]),
            el("td", {}, [el("span", { class: "badge-fail" }, ["failed"])]),
          ]))
        : [el("tr", {}, [el("td", { colspan: "6", class: "muted-sm" }, ["Nothing in the backlog — all provisions succeeded. ✓"])])]),
    ]);
    backlogHost.replaceChildren(head, table);
  }

  // ---- activity log ----
  function outcomeBadge(o?: string | null): HTMLElement {
    if (o === "failed") return el("span", { class: "badge-fail" }, ["failed"]);
    if (o === "success") return el("span", { class: "badge-ok" }, ["success"]);
    return el("span", { class: "muted-sm" }, ["—"]);
  }
  function paintTable(page: AuditPage): void {
    const start = (filters.offset ?? 0) + 1;
    const end = (filters.offset ?? 0) + page.rows.length;
    const head = el("div", { class: "audit-panel-head" }, [
      el("h3", { class: "insights-sub" }, ["Activity log"]),
      el("span", { class: "muted-sm" }, [`${page.total} events · ${page.total ? `${start}–${end}` : "0"}`]),
    ]);
    const table = el("table", { class: "audit-table" }, [
      el("thead", {}, [el("tr", {}, ["Time", "Actor → Impacted", "Event", "App", "Outcome", "IP", "Location"].map((h) => el("th", {}, [h])))]),
      el("tbody", {}, page.rows.length
        ? page.rows.map((r) => el("tr", {}, [
            el("td", { class: "mono-sm" }, [fmtTime(r.created_at)]),
            el("td", {}, [auditWho(r)]),
            el("td", {}, [el("span", { class: "evt-tag" }, [r.event_type])]),
            el("td", {}, [r.app_key || "—"]),
            el("td", {}, [outcomeBadge(r.outcome)]),
            el("td", { class: "mono-sm" }, [r.ip || "—"]),
            el("td", {}, [[r.city, r.country].filter(Boolean).join(", ") || "—"]),
          ]))
        : [el("tr", {}, [el("td", { colspan: "7", class: "muted-sm" }, ["No events match these filters."])])]),
    ]);
    const lim = filters.limit ?? 25;
    const pager = el("div", { class: "audit-pager" }, [
      el("button", { class: "btn-sm", type: "button", disabled: (filters.offset ?? 0) <= 0 ? "" : undefined,
        onclick: () => { filters.offset = Math.max(0, (filters.offset ?? 0) - lim); void refresh(); } }, ["‹ Prev"]),
      el("button", { class: "btn-sm", type: "button", disabled: end >= page.total ? "" : undefined,
        onclick: () => { filters.offset = (filters.offset ?? 0) + lim; void refresh(); } }, ["Next ›"]),
    ]);
    tableHost.replaceChildren(head, table, pager);
  }

  let optionsSeeded = false;
  function seedOptions(s: DashboardStats): void {
    if (optionsSeeded) return;
    optionsSeeded = true;
    for (const r of s.per_app) appSel.append(el("option", { value: r.app_key }, [r.app_key]));
    for (const g of s.geo) ctrySel.append(el("option", { value: g.country }, [g.country]));
  }

  async function refresh(): Promise<void> {
    try {
      const [stats, page, backlog] = await Promise.all([
        api.adminAuditStats(filters),
        api.adminAudit(filters),
        api.adminProvisions(),
      ]);
      seedOptions(stats);
      paintCards(stats);
      void paintCharts(stats);
      paintBacklog(backlog);
      paintTable(page);
    } catch {
      tableHost.replaceChildren(errorBox("Couldn't load the audit data."));
    }
  }

  await refresh();
}
